/**
 * Ready-made block-template sets that can be inserted with one click,
 * and theme presets that change colours, fonts, radii and surface style.
 *
 * Each template = { id, name, description, blocks: [...] }
 * `blocks` items match the BlockRenderer schema: { type, props }.
 *
 * Theme presets carry the following design tokens:
 *   primary_color, secondary_color, text_color, bg_color, accent_color
 *   heading_font, body_font
 *   button_radius : rounded-none | rounded-lg | rounded-2xl | rounded-full
 *   block_radius  : rounded-none | rounded-md | rounded-xl | rounded-2xl | rounded-3xl
 *   card_radius   : same scale as block_radius
 *   surface_style : flat | bordered | elevated | glass | outlined-strong
 *   section_padding : compact | comfortable | spacious
 */

const id = () => Math.random().toString(36).slice(2, 11);

export const BLOCK_TEMPLATES = [
  {
    id: "tpl-hero-fullwidth",
    name: "Hero Full-Width",
    description: "Großes randloses Bild mit Overlay-Titel und CTA.",
    category: "Hero",
    blocks: () => [
      {
        id: id(), type: "image_fullwidth", props: {
          src: "https://images.unsplash.com/photo-1521590832167-7bfc17484d20?q=80&w=2400",
          alt: "",
          overlay_title: "Willkommen bei uns",
          overlay_subtitle: "Premium-Erlebnis mit Liebe zum Detail.",
          height: 520, overlay_opacity: 0.4, text_align: "center",
        },
      },
    ],
  },
  {
    id: "tpl-about",
    name: "Über uns Sektion",
    description: "Überschrift, Beschreibungstext und Bild im Wechselsatz.",
    category: "Inhalt",
    blocks: () => [
      { id: id(), type: "heading", props: { text: "Über uns", level: "h2", align: "left" } },
      { id: id(), type: "text", props: { text: "Wir verbinden Handwerk und Hingabe. Seit Jahren begleiten wir unsere Kunden mit Sorgfalt, Erfahrung und einem Blick fürs Wesentliche. Erfahre mehr über unser Team und unsere Philosophie.", align: "left" } },
      { id: id(), type: "image", props: { src: "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1974", alt: "Über uns", caption: "" } },
    ],
  },
  {
    id: "tpl-three-features",
    name: "Drei-Spalten Features",
    description: "Drei prägnante Vorteile nebeneinander mit Überschrift.",
    category: "Features",
    blocks: () => [
      { id: id(), type: "heading", props: { text: "Was uns auszeichnet", level: "h2", align: "center" } },
      { id: id(), type: "text", props: { text: "✦ Persönliche Beratung  ·  ✦ Hochwertige Produkte  ·  ✦ Faires Preis-Leistungs-Verhältnis", align: "center" } },
    ],
  },
  {
    id: "tpl-team-grid",
    name: "Team Vorstellung",
    description: "Team-Block mit Foto, Rolle, Bio und Social-Media-Kontakten.",
    category: "Team",
    blocks: () => [
      { id: id(), type: "heading", props: { text: "Unser Team", level: "h2", align: "center" } },
      { id: id(), type: "text", props: { text: "Lerne die Menschen kennen, die dich bei uns betreuen.", align: "center" } },
      { id: id(), type: "team", props: { staff_ids: [], columns: 3, show_bio: true, show_contact: true, show_social: true, card_style: "elevated" } },
    ],
  },
  {
    id: "tpl-testimonial-banner",
    name: "Testimonial Banner",
    description: "Volles Hintergrundbild mit Kundenzitat im Vordergrund.",
    category: "Social Proof",
    blocks: () => [
      {
        id: id(), type: "image_fullwidth", props: {
          src: "https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=2400",
          alt: "",
          overlay_title: "Beste Entscheidung des Jahres.",
          overlay_subtitle: "— Lara M., Stammkundin",
          height: 400, overlay_opacity: 0.55, text_align: "center",
        },
      },
    ],
  },
  {
    id: "tpl-cta-block",
    name: "CTA-Block",
    description: "Aufmerksamkeitsstarker Call-to-Action mit Button.",
    category: "CTA",
    blocks: () => [
      { id: id(), type: "spacer", props: { height: 32 } },
      { id: id(), type: "heading", props: { text: "Bereit für deinen Termin?", level: "h2", align: "center" } },
      { id: id(), type: "text", props: { text: "Buche in unter 60 Sekunden online — flexibel und unverbindlich.", align: "center" } },
      { id: id(), type: "button", props: { label: "Jetzt buchen", url: "#booking", variant: "primary" } },
      { id: id(), type: "spacer", props: { height: 32 } },
    ],
  },
  {
    id: "tpl-gallery-strip",
    name: "Bilder-Strip",
    description: "Drei kuratierte Bilder in einer Reihe.",
    category: "Galerie",
    blocks: () => [
      { id: id(), type: "heading", props: { text: "Eindrücke", level: "h2", align: "center" } },
      { id: id(), type: "image", props: { src: "https://images.unsplash.com/photo-1521590832167-7bfc17484d20?q=80&w=1600", alt: "", caption: "" } },
      { id: id(), type: "image", props: { src: "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1600", alt: "", caption: "" } },
    ],
  },
  {
    id: "tpl-contact",
    name: "Kontakt & Anfahrt",
    description: "Kurzer Kontakt-Block mit Überschrift und Beschreibung.",
    category: "Kontakt",
    blocks: () => [
      { id: id(), type: "heading", props: { text: "So findest du uns", level: "h2", align: "left" } },
      { id: id(), type: "text", props: { text: "Musterstraße 1, 10115 Berlin  ·  +49 30 12345678  ·  hi@dein-salon.de", align: "left" } },
      { id: id(), type: "button", props: { label: "Route planen", url: "https://maps.google.com", variant: "secondary" } },
    ],
  },
];

/** Theme presets that salons can pick like Wordpress themes. */
export const THEME_PRESETS = [
  {
    id: "theme-artefact",
    name: "Artefact",
    tagline: "Dunkel, dramatisch, bildlastig — perfekt für Tattoo-Studios, Detailer und urbane Salons.",
    preview_image: "https://images.unsplash.com/photo-1521590832167-7bfc17484d20?q=80&w=1600",
    theme: {
      primary_color: "#0E0E0E",
      secondary_color: "#D4AF37",
      accent_color: "#D4AF37",
      text_color: "#F5F5F5",
      bg_color: "#0A0A0A",
      heading_font: "Bodoni Moda",
      body_font: "Inter",
      button_radius: "rounded-none",
      block_radius: "rounded-none",
      card_radius: "rounded-none",
      surface_style: "outlined-strong",
      section_padding: "spacious",
    },
  },
  {
    id: "theme-haires",
    name: "Haires",
    tagline: "Elegant, hell und einladend — klassisches Salon-Feeling mit Serifen-Eleganz.",
    preview_image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1600",
    theme: {
      primary_color: "#C87A5F",
      secondary_color: "#8B9D83",
      accent_color: "#D6AD60",
      text_color: "#2C2621",
      bg_color: "#FDFBF7",
      heading_font: "Playfair Display",
      body_font: "Manrope",
      button_radius: "rounded-full",
      block_radius: "rounded-3xl",
      card_radius: "rounded-2xl",
      surface_style: "elevated",
      section_padding: "comfortable",
    },
  },
  {
    id: "theme-kitring",
    name: "Kitring",
    tagline: "Modern, mutig, Boutique-Style — typografisch stark mit warmen Akzenten.",
    preview_image: "https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=1600",
    theme: {
      primary_color: "#1A1A1A",
      secondary_color: "#E07856",
      accent_color: "#E07856",
      text_color: "#1F1F1F",
      bg_color: "#FAF7F2",
      heading_font: "DM Serif Display",
      body_font: "DM Sans",
      button_radius: "rounded-lg",
      block_radius: "rounded-xl",
      card_radius: "rounded-lg",
      surface_style: "bordered",
      section_padding: "comfortable",
    },
  },
  {
    id: "theme-atelier",
    name: "Atelier",
    tagline: "Hochwertig, archivarisch, redaktionell — perfekt für Boutique-Concept-Stores.",
    preview_image: "https://images.unsplash.com/photo-1599305090598-fe179d501227?q=80&w=1600",
    theme: {
      primary_color: "#2B2A28",
      secondary_color: "#9A8E7D",
      accent_color: "#B89668",
      text_color: "#1F1E1C",
      bg_color: "#F3EFE6",
      heading_font: "Fraunces",
      body_font: "Lora",
      button_radius: "rounded-none",
      block_radius: "rounded-md",
      card_radius: "rounded-md",
      surface_style: "bordered",
      section_padding: "spacious",
    },
  },
  {
    id: "theme-aurora",
    name: "Aurora",
    tagline: "Frisch, luftig, modern — pastellige Farben für Nagel- und Beauty-Studios.",
    preview_image: "https://images.unsplash.com/photo-1620331311520-246422fd82f9?q=80&w=1600",
    theme: {
      primary_color: "#7C6FE7",
      secondary_color: "#F4A8C7",
      accent_color: "#FFD66B",
      text_color: "#1B1A2E",
      bg_color: "#F8F5FF",
      heading_font: "Bricolage Grotesque",
      body_font: "Space Grotesk",
      button_radius: "rounded-full",
      block_radius: "rounded-3xl",
      card_radius: "rounded-3xl",
      surface_style: "glass",
      section_padding: "comfortable",
    },
  },
  {
    id: "theme-monolith",
    name: "Monolith",
    tagline: "Minimalistisch, brutalist, klare Kanten — Hochglanz-Studio-Optik.",
    preview_image: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?q=80&w=1600",
    theme: {
      primary_color: "#111111",
      secondary_color: "#7A7A7A",
      accent_color: "#FFCB00",
      text_color: "#0A0A0A",
      bg_color: "#FFFFFF",
      heading_font: "Archivo",
      body_font: "IBM Plex Sans",
      button_radius: "rounded-none",
      block_radius: "rounded-none",
      card_radius: "rounded-none",
      surface_style: "flat",
      section_padding: "spacious",
    },
  },
  {
    id: "theme-velvet",
    name: "Velvet",
    tagline: "Sinnlich, dunkel, samtig — für Premium-Friseure und Barber-Studios mit Lounge-Feeling.",
    preview_image: "https://images.unsplash.com/photo-1493804714600-6edb1cd93080?q=80&w=1600",
    theme: {
      primary_color: "#E6C079",
      secondary_color: "#8C6E54",
      accent_color: "#E6C079",
      text_color: "#F1ECE4",
      bg_color: "#1B1411",
      heading_font: "Cormorant Garamond",
      body_font: "Manrope",
      button_radius: "rounded-full",
      block_radius: "rounded-2xl",
      card_radius: "rounded-2xl",
      surface_style: "glass",
      section_padding: "spacious",
    },
  },
];

/** Map of design-token names → resolved CSS pixel/class. */
export const RADIUS_MAP = {
  "rounded-none": "0px",
  "rounded-sm": "4px",
  "rounded-md": "8px",
  "rounded-lg": "12px",
  "rounded-xl": "16px",
  "rounded-2xl": "20px",
  "rounded-3xl": "28px",
  "rounded-full": "9999px",
};

export const SECTION_PADDING_MAP = {
  compact: { x: "1rem", y: "1.5rem" },
  comfortable: { x: "1.5rem", y: "2.5rem" },
  spacious: { x: "2rem", y: "4rem" },
};

/** Returns CSS style object for a surface (card/panel) based on theme tokens. */
export function getSurfaceStyle(theme = {}) {
  const style = theme.surface_style || "elevated";
  const base = {
    borderRadius: RADIUS_MAP[theme.card_radius || "rounded-xl"] || RADIUS_MAP["rounded-xl"],
    background: "#fff",
    transition: "box-shadow .25s ease, transform .25s ease",
  };
  if (style === "flat") return { ...base, border: "none", boxShadow: "none" };
  if (style === "bordered") return { ...base, border: `1px solid ${theme.text_color ? hexAlpha(theme.text_color, 0.12) : "rgba(0,0,0,0.08)"}`, boxShadow: "none" };
  if (style === "elevated") return { ...base, border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 10px 30px rgba(0,0,0,0.06)" };
  if (style === "outlined-strong") return { ...base, border: `2px solid ${theme.text_color || "#111"}`, boxShadow: "none", background: theme.bg_color || "#fff" };
  if (style === "glass") return {
    ...base,
    background: theme.text_color === "#F5F5F5" || theme.text_color === "#F1ECE4" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.55)",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  };
  return base;
}

export function getBlockRadius(theme = {}) {
  return RADIUS_MAP[theme.block_radius || "rounded-2xl"] || RADIUS_MAP["rounded-2xl"];
}

export function getButtonRadius(theme = {}) {
  return RADIUS_MAP[theme.button_radius || "rounded-full"] || RADIUS_MAP["rounded-full"];
}

function hexAlpha(hex, alpha) {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function expandTemplate(tpl) {
  return typeof tpl.blocks === "function" ? tpl.blocks() : (tpl.blocks || []);
}

/** All supported fonts shown in the Theme Studio dropdown. */
export const FONT_LIBRARY = [
  { name: "Playfair Display", cat: "Serif", sample: "Aa" },
  { name: "Cormorant Garamond", cat: "Serif", sample: "Aa" },
  { name: "Fraunces", cat: "Serif", sample: "Aa" },
  { name: "Bodoni Moda", cat: "Serif", sample: "Aa" },
  { name: "DM Serif Display", cat: "Serif", sample: "Aa" },
  { name: "Lora", cat: "Serif", sample: "Aa" },
  { name: "Crimson Pro", cat: "Serif", sample: "Aa" },
  { name: "IBM Plex Serif", cat: "Serif", sample: "Aa" },
  { name: "Libre Caslon Text", cat: "Serif", sample: "Aa" },
  { name: "Marcellus", cat: "Serif", sample: "Aa" },
  { name: "Italiana", cat: "Display", sample: "Aa" },
  { name: "Outfit", cat: "Sans", sample: "Aa" },
  { name: "Manrope", cat: "Sans", sample: "Aa" },
  { name: "DM Sans", cat: "Sans", sample: "Aa" },
  { name: "Inter", cat: "Sans", sample: "Aa" },
  { name: "Space Grotesk", cat: "Sans", sample: "Aa" },
  { name: "Syne", cat: "Display", sample: "Aa" },
  { name: "Bricolage Grotesque", cat: "Display", sample: "Aa" },
  { name: "Archivo", cat: "Sans", sample: "Aa" },
  { name: "IBM Plex Sans", cat: "Sans", sample: "Aa" },
];
